import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import {
  CovCardOverlay,
  CovIcon,
  IconName,
} from "@cx-component-library/react-components";
import styled from "@emotion/styled";
import { Box } from "@mui/material";
import { DiscardProgressDialog } from "components/dialog/DiscardProgressDialog";
import { UnauthenticationDialog } from "components/dialog/UnauthenticationDialog";
import {
  CATEGORY_TAB_ID,
  getVisibleCategoryNavItems,
} from "components/navigation/categoryNavItems";
import { useAutocompleteOptions } from "hooks/autocomplete-data";
import { FILTER_KEY, type FilterType } from "hooks/filter-keys";
import type { FilterEntry } from "hooks/filters";
import { useFeatureFlags } from "hooks/use-config";
import { useEffectEvent } from "hooks/utils/effect-event";
import type { CdcUserAttributes } from "models/cdcUser";
import { useRegisterDiscoveryNav } from "providers/DiscoveryNavProvider";
import { useRegisterNavigationGuard } from "providers/NavigationGuardProvider";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { mapHierarchyToApplicationItems } from "./applicationData";
import { mapBrandDataToItems } from "./brandData";
import { CategoryPanel } from "./CategoryPanel";
import type { CategoryItem } from "./categoryItem";
import { mapEffectDataToItems } from "./effectData";
import {
  ExploreImageProvider,
  useExploreImage,
} from "./explore-image/ExploreImageContext";
import { ExploreImageFlow } from "./explore-image/ExploreImageFlow";
import { ImageUploadPanel } from "./explore-image/upload/ImageUploadPanel";
import { useImageUpload } from "./explore-image/upload/useImageUpload";

const DiscardConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { discard } = useExploreImage();

  const handleConfirm = () => {
    discard();
    onConfirm();
  };

  return (
    <DiscardProgressDialog
      open={open}
      onCancel={onCancel}
      onConfirm={handleConfirm}
    />
  );
};

const NavigationGuardRegistrar = ({ isDirty }: { isDirty: boolean }) => {
  const { discard } = useExploreImage();
  useRegisterNavigationGuard(isDirty, discard);
  return null;
};

/**
 * Feeds an image handed over from the /search sidebar (CX-3746) into the
 * explore-image flow through the same path as a normal drop, so it continues
 * into region-selection. Runs once per handed-over file: the parent passes it
 * a single time (after the image tab opened), and `loadFile` is a stable
 * event callback.
 */
const InitialImageLoader = ({ file }: { file: File | null }) => {
  const { handleFilesDropped } = useImageUpload();
  const loadFile = useEffectEvent(handleFilesDropped);

  useEffect(() => {
    if (!file) {
      return;
    }
    const transfer = new DataTransfer();
    transfer.items.add(file);
    loadFile(transfer.files);
  }, [file, loadFile]);

  return null;
};

const CategoryGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "8px",
  padding: "16px",
  "@media (min-width: 600px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
    padding: "24px 32px",
  },
  "@media (min-width: 1200px)": {
    gridTemplateColumns: "repeat(3, 1fr)",
    padding: "24px 88px",
  },
});

const CardWrapper = styled.div({
  "&:hover .cov-card-overlay__icon-btn, & .cov-card-overlay--hovered .cov-card-overlay__icon-btn":
    {
      backgroundColor: "var(--card-accent-color)",
    },
});

const CategoryCardGrid = ({
  items,
  onSelect,
}: {
  items: CategoryItem[];
  onSelect: (filterValue: AutocompleteData) => void;
}) => {
  const { t } = useTranslation();
  return (
    <CategoryGrid>
      {items.map((item) => (
        <CardWrapper
          key={item.id}
          style={
            {
              "--card-accent-color": item.accentColor,
            } as React.CSSProperties
          }
        >
          <CovCardOverlay
            image={item.image}
            header={item.name}
            description={item.description}
            icon={<CovIcon iconName={IconName.KEYBOARD_ARROW_RIGHT} />}
            onCardClick={() => onSelect(item.filterValue)}
            actionLabel={t("ldp_discovery_view_result")}
            actionIcon={<CovIcon iconName={IconName.KEYBOARD_ARROW_RIGHT} />}
            accentColor={item.accentColor}
          />
        </CardWrapper>
      ))}
    </CategoryGrid>
  );
};

const AUTH_REQUIRED_TABS: string[] = [
  CATEGORY_TAB_ID.IMAGE,
  CATEGORY_TAB_ID.APPLICATION,
  CATEGORY_TAB_ID.EFFECT,
  CATEGORY_TAB_ID.BRAND,
];

export const DiscoverySection = ({
  isLogin,
  onFlowStepChange,
  enableListView,
  defaultToEffect,
  initialTabId,
  initialImageFile,
  inlinePanel,
}: {
  isLogin: boolean;
  onFlowStepChange?: (step: string) => void;
  enableListView?: boolean;
  defaultToEffect?: boolean;
  /** Reopens this tab on mount, e.g. when returning here via the discovery
   * nav from another discovery page (CX-3737). Takes precedence over
   * defaultToEffect and goes through the same auth gate as any other tab
   * selection. */
  initialTabId?: string | null;
  /** Image dropped on the /search sidebar (CX-3746). Fed into the Upload &
   * Match flow once, when it arrives, and only if `initialTabId` is the image
   * tab and the tab opened (i.e. the user is authenticated); otherwise it is
   * discarded. The caller may supply it after mount. */
  initialImageFile?: File | null;
  inlinePanel?: boolean;
}) => {
  const flags = useFeatureFlags();
  const uploadMatchEnabled = enableListView ?? flags.uploadMatch;
  const browseEnabled = flags.discoveryBrowse;
  const navItems = useMemo(
    () => getVisibleCategoryNavItems(uploadMatchEnabled, browseEnabled),
    [uploadMatchEnabled, browseEnabled],
  );
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  useEffect(() => {
    if (defaultToEffect && isLogin) {
      setActiveTabId((prev) => prev ?? CATEGORY_TAB_ID.EFFECT);
    }
  }, [defaultToEffect, isLogin]);

  const [showResultsOnly, setShowResultsOnly] = useState(false);
  const [flowStep, setFlowStep] = useState("idle");
  const flowLocked =
    flowStep === "region-selection" ||
    flowStep === "analysis" ||
    flowStep === "results";
  const [openAuthDialog, setOpenAuthDialog] = useState(false);
  const [pendingTabId, setPendingTabId] = useState<string | null | undefined>(
    undefined,
  );
  const navigate = useNavigate();
  const postDiscardTabRef = useRef<string | null>(null);
  const { hierachyData } = useContext<SettingsContextValue>(SettingsContext);

  const applicationItems = useMemo(
    () => mapHierarchyToApplicationItems(hierachyData),
    [hierachyData],
  );

  const [brandData] = useAutocompleteOptions("brand", undefined, undefined);
  const brandItems = useMemo(
    () => mapBrandDataToItems(brandData ?? []),
    [brandData],
  );

  const [effectData] = useAutocompleteOptions(
    "colorEffects",
    undefined,
    undefined,
  );
  const effectItems = useMemo(
    () => mapEffectDataToItems(effectData ?? []),
    [effectData],
  );

  const isAuthenticated = useMemo(() => {
    const cdcUser: CdcUserAttributes | undefined =
      getDataFromLocalstorageByKey("cdcUser") ?? undefined;
    return isLogin || Boolean(cdcUser);
  }, [isLogin]);

  // Shared gate for both tab actions: swallow the request into pendingTabId
  // while an explore-image flow is running, and open the auth dialog instead
  // of a panel for an auth-required tab while logged out. Returns whether the
  // caller may proceed to change the active tab.
  const canOpenTab = useCallback(
    (id: string | null) => {
      if (flowLocked) {
        setPendingTabId(id);
        return false;
      }
      if (id && AUTH_REQUIRED_TABS.includes(id) && !isAuthenticated) {
        setOpenAuthDialog(true);
        return false;
      }
      return true;
    },
    [isAuthenticated, flowLocked],
  );

  // Nav-bar clicks toggle: clicking the open tab again closes its panel.
  const handleTabSelect = useCallback(
    (id: string | null) => {
      if (!canOpenTab(id)) {
        return;
      }
      setActiveTabId((prev) => (prev === id ? null : id));
    },
    [canOpenTab],
  );

  // Arriving from another discovery page (CX-3737) must deterministically
  // OPEN the requested tab, never toggle it — React 18 StrictMode invokes the
  // initialTabId effect twice on mount in dev, so a toggle would open then
  // immediately re-close the panel, leaving the user on an empty page.
  const openTab = useCallback(
    (id: string | null) => {
      if (!canOpenTab(id)) {
        return false;
      }
      setActiveTabId(id);
      return true;
    },
    [canOpenTab],
  );
  const [imageTabOpened, setImageTabOpened] = useState(false);

  const closePanel = useCallback(() => setActiveTabId(null), []);

  // Applies initialTabId exactly once per requested tab. openTab is memoized
  // on [isAuthenticated, flowLocked], both of which can change shortly after
  // mount (e.g. isAuthenticated flipping once a CDC/cdcUser session hydrates)
  // — listing it as a dependency directly would re-run this effect on that
  // change. useEffectEvent reads the latest openTab without making it a
  // dependency, so this only re-fires when initialTabId itself changes.
  const handleInitialTabId = useEffectEvent((id: string) => {
    if (openTab(id) && id === CATEGORY_TAB_ID.IMAGE) {
      setImageTabOpened(true);
    }
  });
  useEffect(() => {
    if (initialTabId) {
      handleInitialTabId(initialTabId);
    }
  }, [initialTabId, handleInitialTabId]);

  const handleFlowStepChange = useCallback(
    (step: string) => {
      setFlowStep(step);
      const isResults = step === "results";
      setShowResultsOnly(isResults);
      if (step === "idle") {
        const targetTab = postDiscardTabRef.current ?? CATEGORY_TAB_ID.IMAGE;
        postDiscardTabRef.current = null;
        setActiveTabId(targetTab);
      } else {
        setActiveTabId(null);
      }
      onFlowStepChange?.(step);
    },
    [onFlowStepChange],
  );

  const { scrollIntoView: scrollToNav } = useRegisterDiscoveryNav({
    items: navItems,
    activeId: showResultsOnly ? CATEGORY_TAB_ID.IMAGE : activeTabId,
    onSelect: handleTabSelect,
  });

  // Arriving from another discovery page via the nav (CX-3737) reopens the
  // requested tab, but its panel mounts below the banner/carousel — without
  // this the user is left at the top of the page and the switch looks like
  // nothing happened. Scroll the nav (and the panel directly under it) into
  // view whenever a tab is requested on mount.
  useEffect(() => {
    if (initialTabId) {
      scrollToNav();
    }
  }, [initialTabId, scrollToNav]);

  const navigateWithFilter = useCallback(
    (filterType: FilterEntry["type"], filterValue: AutocompleteData) => {
      const filters: FilterEntry[] = [
        { type: filterType, value: [filterValue] },
      ];
      navigate("/search", { state: { filters } });
    },
    [navigate],
  );

  const handleApplicationSelect = useCallback(
    (value: AutocompleteData) => navigateWithFilter(FILTER_KEY.INDUSTRY, value),
    [navigateWithFilter],
  );

  const handleEffectSelect = useCallback(
    (value: AutocompleteData) => {
      const filterKey =
        (value.type as FilterType | undefined) ?? FILTER_KEY.COLOR_EFFECT;
      navigateWithFilter(filterKey, value);
    },
    [navigateWithFilter],
  );

  const handleBrandSelect = useCallback(
    (value: AutocompleteData) => navigateWithFilter(FILTER_KEY.BRAND, value),
    [navigateWithFilter],
  );

  return (
    <ExploreImageProvider>
      <NavigationGuardRegistrar isDirty={flowLocked} />
      {uploadMatchEnabled && (
        <InitialImageLoader
          file={imageTabOpened ? (initialImageFile ?? null) : null}
        />
      )}

      {!showResultsOnly && (
        <Box sx={{ position: "relative" }}>
          {uploadMatchEnabled && (
            <CategoryPanel
              open={activeTabId === CATEGORY_TAB_ID.IMAGE}
              onClose={closePanel}
              inline={inlinePanel}
            >
              <ImageUploadPanel />
            </CategoryPanel>
          )}

          {browseEnabled && (
            <>
              <CategoryPanel
                open={activeTabId === CATEGORY_TAB_ID.APPLICATION}
                onClose={closePanel}
                inline={inlinePanel}
              >
                <CategoryCardGrid
                  items={applicationItems}
                  onSelect={handleApplicationSelect}
                />
              </CategoryPanel>

              <CategoryPanel
                open={activeTabId === CATEGORY_TAB_ID.EFFECT}
                onClose={closePanel}
                inline={inlinePanel}
              >
                <CategoryCardGrid
                  items={effectItems}
                  onSelect={handleEffectSelect}
                />
              </CategoryPanel>

              <CategoryPanel
                open={activeTabId === CATEGORY_TAB_ID.BRAND}
                onClose={closePanel}
                inline={inlinePanel}
              >
                <CategoryCardGrid
                  items={brandItems}
                  onSelect={handleBrandSelect}
                />
              </CategoryPanel>
            </>
          )}
        </Box>
      )}

      {uploadMatchEnabled && (
        <ExploreImageFlow
          onFlowStepChange={handleFlowStepChange}
          onScrollToNav={scrollToNav}
        />
      )}

      <UnauthenticationDialog
        isOpen={openAuthDialog}
        onClose={setOpenAuthDialog}
      />
      <DiscardConfirmDialog
        open={pendingTabId !== undefined}
        onCancel={() => setPendingTabId(undefined)}
        onConfirm={() => {
          const tab = pendingTabId ?? null;
          setPendingTabId(undefined);
          if (tab && AUTH_REQUIRED_TABS.includes(tab) && !isAuthenticated) {
            setOpenAuthDialog(true);
            return;
          }
          postDiscardTabRef.current = tab;
        }}
      />
    </ExploreImageProvider>
  );
};
